<template lang="pug">
Sheet(v-if="canEditAnchorPeriod" v-model:open="sheetOpen")
  SheetTrigger(as-child)
    Button.ml-2(
      type="button"
      variant="link"
      size="sm"
      class="h-auto px-1 py-0 align-baseline text-xs"
      title="编辑 Anchor 周期"
    )
      Icon(name="material-symbols:edit-calendar-outline-rounded" class="mr-1 h-3.5 w-3.5")
      | 编辑 Anchor

  SheetContent(
    side="right"
    class="flex h-full w-[min(94vw,520px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]"
  )
    SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
      SheetTitle 编辑 Anchor 周期
      SheetDescription 修改当前计费周期；所有日期时间均按北京时间解释。

    form(class="flex min-h-0 flex-1 flex-col" @submit.prevent="saveAnchorPeriod")
      div(class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7")
        section.rounded-lg.border.p-4(class="bg-muted/30")
          div.flex.items-center.justify-between.gap-3
            div
              p.text-xs.text-muted-foreground 计费模式
              p.mt-1.text-sm.font-semibold anchor · 按订阅日计费
            span.rounded-full.border.px-2.py-1.font-mono.text-xs.text-muted-foreground
              | service ID {{ service.id }}

        div.space-y-2
          Label(:for="fieldId('period-start')") 周期开始（北京时间）
          Input(
            :id="fieldId('period-start')"
            v-model="periodStartInput"
            type="datetime-local"
            step="0.001"
          )
          p.text-xs.text-muted-foreground 当前周期必须从这个时刻开始，并覆盖现在。

        div.space-y-2
          Label(:for="fieldId('period-end')") 周期结束（北京时间）
          Input(
            :id="fieldId('period-end')"
            :model-value="periodEndInput"
            type="datetime-local"
            step="0.001"
            @update:model-value="updatePeriodEnd"
          )
          p.text-xs.text-muted-foreground 修改结束日期会同步锚定日；短月会保留 29–31 日锚点。
          p.text-xs.text-muted-foreground(v-if="maxPeriodEndText") 最晚可设为 {{ maxPeriodEndText }}。

        div.space-y-2
          Label(:for="fieldId('anchor-day')") 锚定日
          Input(
            :id="fieldId('anchor-day')"
            :model-value="anchorDayInput"
            type="number"
            min="1"
            max="31"
            step="1"
            title="每个月几号重置流量"
            @update:model-value="updateAnchorDay"
          )
          p.text-xs.text-muted-foreground 取值 1–31；修改后会同步周期结束日期，短月落在月末。

        div.rounded-md.border.p-3.text-sm.text-destructive(
          v-if="formError"
          class="border-destructive/30 bg-destructive/5"
          role="alert"
        ) {{ formError }}

      SheetFooter(class="shrink-0 border-t px-5 py-4 sm:px-7")
        Button(type="button" variant="outline" :disabled="saving" @click="sheetOpen = false") 取消
        Button(type="submit" :disabled="saving || Boolean(formError)")
          Icon(v-if="saving" name="mdi:loading" class="mr-1.5 h-4 w-4 animate-spin")
          | {{ saving ? '保存中…' : '保存 Anchor 周期' }}
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { toast } from '~/utils/sonner'

interface AnchorPeriodFormCheck {
  periodStart: number
  periodEnd: number
  anchorDay: number
  maxPeriodEnd: number
  now: number
  periodEndMatchesAnchorDay?: boolean
}

interface AnchorPeriodResponse {
  service_id: number
  billing_mode: 'anchor'
  period_start: string
  period_end: string
  anchor_day: number
}

function anchorPeriodFormError(input: AnchorPeriodFormCheck) {
  if (
    !Number.isFinite(input.periodStart) ||
    !Number.isFinite(input.periodEnd) ||
    !Number.isFinite(input.maxPeriodEnd)
  ) {
    return '请填写有效的周期开始和周期结束时间'
  }
  if (!Number.isInteger(input.anchorDay) || input.anchorDay < 1 || input.anchorDay > 31) {
    return '锚定日必须是 1 到 31 的整数'
  }
  if (input.periodEnd <= input.periodStart) {
    return '周期结束必须晚于周期开始'
  }
  if (input.periodStart > input.now) {
    return '周期开始不能晚于现在'
  }
  if (input.periodEnd <= input.now) {
    return '周期结束必须晚于现在'
  }
  if (input.periodEndMatchesAnchorDay === false) {
    return '周期结束日期必须与锚定日一致；短月可以使用月末'
  }
  if (input.periodEnd > input.maxPeriodEnd) {
    return '周期结束不能晚于周期开始后的一个锚定月'
  }
  return null
}

const props = defineProps<{
  service: userServicesAttributes
}>()

const dayjs = useDayjs()
const serviceStore = useUserService()
const sheetOpen = ref(false)
const saving = ref(false)
const periodStartInput = ref('')
const periodEndInput = ref('')
const anchorDayInput = ref<string | number>('')
const compId = useId()
const fieldId = (name: string) => `${compId}-${name}`

const canEditAnchorPeriod = computed(() => props.service.billing_mode === 'anchor')

function toBeijingInput(value: string | null | undefined) {
  const parsed = dayjs(value)
  return parsed.isValid()
    ? parsed.tz(BILLING_TZ).format('YYYY-MM-DDTHH:mm:ss.SSS')
    : ''
}

function parseBeijingInputValue(value: string) {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  )
  if (!match) {
    return NaN
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6] || 0)
  const millisecond = Number((match[7] || '').padEnd(3, '0') || 0)
  if (year < 1000 || year > 9999) {
    return NaN
  }

  const beijingOffsetMs = 8 * 60 * 60 * 1000
  const timestamp = Date.UTC(
    year,
    month - 1,
    day,
    hour - 8,
    minute,
    second,
    millisecond,
  )
  const local = new Date(timestamp + beijingOffsetMs)
  if (
    !Number.isFinite(timestamp) ||
    local.getUTCFullYear() !== year ||
    local.getUTCMonth() !== month - 1 ||
    local.getUTCDate() !== day ||
    local.getUTCHours() !== hour ||
    local.getUTCMinutes() !== minute ||
    local.getUTCSeconds() !== second ||
    local.getUTCMilliseconds() !== millisecond
  ) {
    return NaN
  }
  return timestamp
}

function linkedAnchorDayForPeriodEnd(
  periodEndDay: number,
  daysInMonth: number,
  currentAnchorDay: number,
) {
  return periodEndDay === Math.min(currentAnchorDay, daysInMonth)
    ? currentAnchorDay
    : periodEndDay
}

function resetForm() {
  periodStartInput.value = toBeijingInput(props.service.period_start)
  periodEndInput.value = toBeijingInput(props.service.period_end)
  anchorDayInput.value = props.service.anchor_day ?? ''
  updatePeriodEnd(periodEndInput.value)
}

const periodStartMs = computed(() => parseBeijingInputValue(periodStartInput.value))
const periodEndMs = computed(() => parseBeijingInputValue(periodEndInput.value))
const anchorDay = computed(() => Number(anchorDayInput.value))

function updatePeriodEnd(value: string | number) {
  periodEndInput.value = String(value)
  if (!Number.isFinite(periodEndMs.value)) {
    return
  }

  const end = dayjs(periodEndMs.value).tz(BILLING_TZ)
  anchorDayInput.value = linkedAnchorDayForPeriodEnd(
    end.date(),
    end.daysInMonth(),
    anchorDay.value,
  )
}

function updateAnchorDay(value: string | number) {
  anchorDayInput.value = value
  const nextAnchorDay = Number(value)
  if (
    !Number.isFinite(periodEndMs.value) ||
    !Number.isInteger(nextAnchorDay) ||
    nextAnchorDay < 1 ||
    nextAnchorDay > 31
  ) {
    return
  }

  const linkedEnd = addAnchoredMonths(dayjs, new Date(periodEndMs.value), 0, nextAnchorDay)
  periodEndInput.value = toBeijingInput(linkedEnd.toISOString())
}

const maxPeriodEndMs = computed(() => {
  if (!Number.isFinite(periodStartMs.value) || !Number.isInteger(anchorDay.value) || anchorDay.value < 1 || anchorDay.value > 31) {
    return NaN
  }
  return +addAnchoredMonths(dayjs, new Date(periodStartMs.value), 1, anchorDay.value)
})
const periodEndMatchesAnchorDay = computed(() => {
  if (!Number.isFinite(periodEndMs.value) || !Number.isInteger(anchorDay.value) || anchorDay.value < 1 || anchorDay.value > 31) {
    return false
  }
  const end = dayjs(periodEndMs.value).tz(BILLING_TZ)
  return end.date() === Math.min(anchorDay.value, end.daysInMonth())
})

function currentFormError(now = Date.now()) {
  return anchorPeriodFormError({
    periodStart: periodStartMs.value,
    periodEnd: periodEndMs.value,
    anchorDay: anchorDay.value,
    maxPeriodEnd: maxPeriodEndMs.value,
    now,
    periodEndMatchesAnchorDay: periodEndMatchesAnchorDay.value,
  })
}

const formError = computed(() => currentFormError())
const maxPeriodEndText = computed(() => Number.isFinite(maxPeriodEndMs.value)
  ? dayjs(maxPeriodEndMs.value).tz(BILLING_TZ).format('YYYY年MM月DD日 HH:mm:ss.SSS')
  : '')

watch(sheetOpen, (open) => {
  if (open) {
    resetForm()
  }
})
watch(() => props.service.id, resetForm)
resetForm()

async function saveAnchorPeriod() {
  const validationError = currentFormError()
  if (validationError) {
    toast({ title: 'Anchor 周期不正确', description: validationError, variant: 'destructive' })
    return
  }

  saving.value = true
  try {
    const response = await useAPI().put(
      `users/${useUser().id}/services/${props.service.id}/anchor_period`,
      {
        body: {
          period_start: new Date(periodStartMs.value).toISOString(),
          period_end: new Date(periodEndMs.value).toISOString(),
          anchor_day: anchorDay.value,
        },
      },
    ) as AnchorPeriodResponse
    if (Number(response?.service_id) !== Number(props.service.id)) {
      throw new Error('Anchor 周期接口返回了错误的服务')
    }

    const currentService = serviceStore.currentService
    if (Number(currentService?.id) === Number(response.service_id)) {
      currentService.billing_mode = response.billing_mode
      currentService.period_start = response.period_start
      currentService.period_end = response.period_end
      currentService.anchor_day = response.anchor_day
      currentService.extra.buy_time = response.period_start
    }
    toast({ title: 'Anchor 周期已更新', description: '新的周期边界已经生效' })
    sheetOpen.value = false
  } catch (error) {
    toast({
      title: 'Anchor 周期更新失败',
      description: error instanceof Error ? error.message : String(error),
      variant: 'destructive',
    })
  } finally {
    saving.value = false
  }
}
</script>
